import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, ChevronDown, LogOut, Ship, Settings, Shield, Zap } from "lucide-react";
import { useOrganization } from "@/hooks/useOrganization";
import Logo from "@/components/Logo";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { trackNavigation, trackCTAClick, trackOutboundLink } from "@/utils/analytics";
import { useAuth } from "@/hooks/useAuth";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { MobileSheetMenu } from "@/components/mobile/MobileSheetMenu";
import { hapticFeedback } from "@/hooks/useHapticFeedback";

const navigationItems = [
  {
    name: "Yacht Valuation",
    href: "/valuation",
    submenu: [
      { name: "Instant Valuation", href: "/valuation", isBold: true },
      { name: "Professional Yacht Appraisal", href: "/boat-appraisal" },
      { name: "AI Yacht Valuation", href: "/ai-boat-valuation" },
      { 
        name: "By Type",
        submenu: [
          { name: "Motor Yacht Values", href: "/boat-value/motor-yachts" },
          { name: "Sport Yacht Values", href: "/boat-value/sport-yachts" },
          { name: "Flybridge Yacht Values", href: "/boat-value/flybridge-yachts" },
          { name: "Express Cruiser Values", href: "/boat-value/express-cruisers" },
          { name: "Trawler Yacht Values", href: "/boat-value/trawler-yachts" },
          { name: "Sailboat Values", href: "/boat-value/sailboats" },
          { name: "Catamaran Values", href: "/boat-value/catamarans" },
          { name: "Center Console Values", href: "/boat-value/center-console-boats" },
        ]
      }
    ]
  },
  {
    name: "Resources",
    href: "/resources",
    submenu: [
      { name: "Market Intelligence", href: "/resources", isBold: true },
      { name: "Yacht Depreciation", href: "/guides/boat-depreciation" },
      { name: "Yacht Buying Guide", href: "/guides/boat-buying" },
      { name: "Yacht Selling Guide", href: "/guides/boat-selling" },
      { name: "FAQs", href: "/resources/faqs" },
      { name: "---", isDivider: true },
      { name: "About YachtValues", href: "/about" },
      { name: "Contact", href: "/contact" },
    ],
  },
  { name: "Insights", href: "/blog" },
];

export default function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile, isAuthenticated, signOut } = useAuth();
  const { isAdmin } = useOrganization();

  const isActive = (href: string) => location.pathname === href;

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const userInitials = profile?.first_name && profile?.last_name
    ? `${profile.first_name[0]}${profile.last_name[0]}`.toUpperCase()
    : user?.email?.[0]?.toUpperCase() || 'U';

  return (
    <header className="sticky top-0 z-50 w-full bg-background backdrop-blur supports-[backdrop-filter]:bg-background/95 border-b border-border/30 mb-0">
      <div className="container mx-auto px-4 border-b-0 mb-0">
        <div className="flex h-32 items-center justify-between border-b-0 mb-0">
          {/* Left Nav */}
          <nav className="hidden md:flex items-center space-x-4 lg:space-x-6 flex-1">
            {navigationItems.map((item) => (
              <div key={item.name}>
                {item.submenu ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        className="flex items-center space-x-1 text-foreground/70 hover:text-primary"
                      >
                        <span>{item.name}</span>
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-64 bg-card border-border z-50">
                      {item.submenu.map((subItem) => (
                        subItem.isDivider ? (
                          <DropdownMenuSeparator key={subItem.name} />
                        ) : subItem.submenu ? (
                          <DropdownMenu key={subItem.name}>
                            <DropdownMenuTrigger asChild>
                              <div className="flex items-center justify-between px-3 py-2 text-sm hover:bg-muted rounded-md cursor-pointer text-foreground/80">
                                <span>{subItem.name}</span>
                                <ChevronDown className="h-4 w-4 rotate-[-90deg]" />
                              </div>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent side="right" className="w-64 max-h-96 overflow-y-auto bg-card border-border z-50">
                              {subItem.submenu.map((nestedItem) => (
                                <DropdownMenuItem key={nestedItem.name} asChild>
                                  <Link
                                    to={nestedItem.href}
                                    className="block px-3 py-2 text-sm hover:bg-muted rounded-md cursor-pointer text-foreground/80"
                                  >
                                    {nestedItem.name}
                                  </Link>
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        ) : (
                          <DropdownMenuItem key={subItem.name} asChild>
                            <Link
                              to={subItem.href}
                              className={`block px-3 py-2 text-sm hover:bg-muted rounded-md cursor-pointer text-foreground/80 ${subItem.isBold ? 'font-bold' : ''}`}
                            >
                              {subItem.name}
                            </Link>
                          </DropdownMenuItem>
                        )
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <Link
                    to={item.href}
                    className={`text-sm font-medium transition-colors hover:text-primary ${
                      isActive(item.href)
                        ? "text-primary border-b border-primary"
                        : "text-foreground/70"
                    }`}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* Centered Logo */}
          <Link to="/" className="flex items-center justify-center absolute left-1/2 -translate-x-1/2">
            <Logo />
          </Link>

          {/* Spacer for layout balance (nav is now on the left) */}

          {/* CTA Button + User Menu */}
          <div className="hidden md:flex items-center gap-2">
            <Button 
              asChild 
              className="bg-primary/90 hover:bg-primary text-primary-foreground border-0 font-medium"
              onClick={() => trackCTAClick('header_request_valuation', '/valuation', 'header')}
            >
              <Link to="/valuation">Instant Estimate</Link>
            </Button>

            {isAdmin && (
              <Button 
                asChild 
                variant="ghost" 
                size="icon" 
                className="text-foreground/50 hover:text-foreground hover:bg-muted"
                title="Admin Dashboard"
              >
                <Link to="/admin">
                  <Shield className="h-5 w-5" />
                </Link>
              </Button>
            )}

            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-9 w-9 rounded-full">
                    <Avatar className="h-9 w-9">
                      <AvatarFallback className="bg-primary/10 text-primary">
                        {userInitials}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-card border-border">
                  <div className="flex items-center justify-start gap-2 p-2">
                    <div className="flex flex-col space-y-1 leading-none">
                      {profile?.first_name && (
                        <p className="font-medium text-foreground">{profile.first_name} {profile.last_name}</p>
                      )}
                      <p className="w-[200px] truncate text-sm text-muted-foreground">
                        {user?.email}
                      </p>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/my-portfolio" className="w-full cursor-pointer">
                      <Ship className="mr-2 h-4 w-4" />
                      My Portfolio
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/account-settings" className="w-full cursor-pointer">
                      <Settings className="mr-2 h-4 w-4" />
                      Account Settings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer text-destructive">
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button asChild variant="ghost" className="text-foreground/70 hover:text-foreground font-medium">
                <Link to="/auth">Sign In</Link>
              </Button>
            )}
          </div>

          {/* Mobile Menu Trigger */}
          <Button 
            variant="ghost" 
            size="icon" 
            className="md:hidden text-foreground/70 hover:text-primary"
            onClick={() => {
              hapticFeedback('light');
              setIsMobileMenuOpen(true);
            }}
          >
            <Menu className="h-6 w-6" />
          </Button>
        </div>
      </div>

      {/* Mobile Sheet Menu */}
      <MobileSheetMenu
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        navigationItems={navigationItems}
        isAuthenticated={isAuthenticated}
        user={user}
        profile={profile}
        onSignOut={handleSignOut}
        isAdmin={isAdmin}
      />
    </header>
  );
}
